import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { get } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ALERT_LEVEL } from "@/lib/status";
import { fmtDate, fmtPct } from "@/lib/format";
import { historiqueDepuisTendances } from "@/lib/alertes";
import { Panel } from "@/components/common/Panel";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState, EmptyState } from "@/components/common/States";

const AXIS = { fill: "#94a3b8", fontSize: 10, fontFamily: "JetBrains Mono" };

const columns = [
  { key: "date", header: "Date", mono: true, render: (r) => fmtDate(r.date) },
  { key: "niveau", header: "Niveau", render: (r) => <StatusBadge config={ALERT_LEVEL} code={r.niveau} /> },
  { key: "ligne", header: "Ligne", cellClassName: "font-semibold" },
  { key: "type", header: "Type", cellClassName: "font-mono text-[11px] text-muted-foreground" },
  { key: "valeur", header: "Valeur", align: "right", render: (r) => <span className={cn(r.niveau === "rouge" ? "text-red-400" : "text-amber-400")}>{fmtPct(r.valeur)}</span> },
  { key: "texte", header: "Détail", cellClassName: "text-muted-foreground" },
];

export const HistoriqueAlertes = ({ lignes }) => {
  const [jours, setJours] = useState(14);
  const [type, setType] = useState("");
  const qs = useQueries({ queries: lignes.map((l) => ({ queryKey: ["trs-tendance", jours, l.ligne_id], queryFn: () => get("/trs/tendance", { jours, ligne_id: l.ligne_id }) })) });
  const loading = qs.some((q) => q.isLoading);
  const hist = useMemo(() => historiqueDepuisTendances(lignes.map((l, i) => ({ ligne: l.ligne, points: qs[i].data }))), [lignes, qs.map((q) => q.dataUpdatedAt).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = type ? hist.filter((h) => h.type === type) : hist;
  const parJour = useMemo(() => {
    const m = {};
    hist.forEach((h) => { m[h.date] = m[h.date] || { date: h.date, rouge: 0, orange: 0 }; m[h.date][h.niveau] += 1; });
    return Object.values(m).sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [hist]);
  const nbRouge = hist.filter((h) => h.niveau === "rouge").length;
  return (
    <Panel title="Historique des alertes" subtitle={`${jours} derniers jours · ${hist.length} alertes dont ${nbRouge} critiques`} testId="historique-alertes-panel" bodyClassName="p-0"
      actions={<>
        {[7, 14, 30].map((j) => <button key={j} data-testid={`historique-range-${j}`} onClick={() => setJours(j)} className={cn("h-6 px-2 text-[11px] font-mono border rounded-sm", jours === j ? "bg-accent border-border text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{j}j</button>)}
        <select data-testid="historique-type" value={type} onChange={(e) => setType(e.target.value)} className="h-6 bg-input border border-border rounded-sm text-[11px] px-1.5 ml-1">
          <option value="">Tous types</option><option value="TRS">TRS</option><option value="Performance">Performance</option><option value="Qualité">Qualité</option>
        </select>
      </>}>
      {loading ? <LoadingState rows={4} /> : hist.length === 0 ? <EmptyState title="Aucune alerte sur la période" hint="Aucun jour sous les seuils TRS / performance / qualité." /> : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.6fr]">
          <div className="h-52 p-2 border-b xl:border-b-0 xl:border-r border-border/60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={parJour} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={AXIS} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", fontSize: 11, fontFamily: "JetBrains Mono" }} labelFormatter={fmtDate} />
                <Bar dataKey="rouge" name="Critiques" stackId="a" fill="#ef4444" />
                <Bar dataKey="orange" name="Vigilance" stackId="a" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="max-h-80 overflow-auto"><DataTable testId="historique-table" columns={columns} rows={rows} rowKey={(r) => `${r.date}-${r.ligne}-${r.type}`} emptyTitle="Aucune alerte pour ce type" /></div>
        </div>
      )}
      <div className="px-3 py-1.5 border-t border-border/60 text-[10.5px] text-muted-foreground">Historique reconstitué à partir du TRS journalier par ligne (moteur V2.39, seuils TRS &lt; 60 %, Perf &lt; 70 %, Qualité &lt; 95 %).</div>
    </Panel>
  );
};
